import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Truck, Users, Warehouse, Wallet, Trophy, Save, LogOut, Target, Globe, UserCircle, ToggleLeft, ToggleRight, Scale, Landmark, Swords, ChevronRight, Sun, Moon, MonitorSmartphone, Maximize2, Minimize2, Briefcase, LineChart, Inbox, FileSpreadsheet, Brain, Crosshair, FileSignature, CalendarDays, Volume2, VolumeX, Sparkles, Bug, FileText, FileBadge } from 'lucide-react';
import AdvisorInsightsPanel from '../game/AdvisorInsightsPanel';
import TradeShowModal from '../game/TradeShowModal';
import GameNotifications from '../game/GameNotifications';
import MilestoneVideoModal from '../MilestoneVideoModal';
import SoundToggle from '../game/SoundToggle';
import SaveGameModal from '../game/SaveGameModal';
import CompanyLogo from '../game/CompanyLogo';
import PostponedEventsBadge from '../game/PostponedEventsBadge';
import ContextualTutoCard from '../game/ContextualTutoCard';
import ContextualTutoTriggers from '../game/ContextualTutoTriggers';
import BugReportModal from '../BugReportModal';
import EndGameSummaryModal from '../EndGameSummaryModal';
import useAudioStore from '../../store/audioStore';
import useGameStore from '../../store/gameStore';
import useLicenseStore from '../../store/licenseStore';
import { EMPTY_OBJECT } from '../../store/stableEmpties';
import useThemeStore from '../../store/themeStore';
import { useTranslation, useLanguageStore } from '../../i18n';
import { useGameSounds } from '../../hooks/useGameSounds';
import { ADVISORS } from '../../data/advisors';
import { overdraftSummary } from '../../store/helpers/overdraftHelper';
import HSTLogo from '../HSTLogo';

const NAV_ITEMS = [
  { path: '/', labelKey: 'nav_dashboard', icon: LayoutDashboard, group: 'main' },
  { path: '/messagerie', labelKey: 'nav_messagerie', icon: Inbox, group: 'main', badge: 'inbox' },
  { path: '/fleet', labelKey: 'nav_fleet', icon: Truck, group: 'main' },
  { path: '/hr', labelKey: 'nav_hr', icon: Users, group: 'main' },
  { path: '/fonctions-support', labelKey: 'nav_fonctions_support', icon: Briefcase, group: 'main' },
  { path: '/depot', labelKey: 'nav_depot', icon: Warehouse, group: 'main' },
  { path: '/agenda', labelKey: 'nav_agenda', icon: CalendarDays, group: 'main', requiresAgenda: true },
  { path: '/finance', labelKey: 'nav_finance', icon: Wallet, group: 'finance' },
  { path: '/fiscalite', labelKey: 'nav_fiscality', icon: Scale, group: 'finance', badge: 'tax' },
  { path: '/banque', labelKey: 'nav_bank', icon: Landmark, group: 'finance' },
  { path: '/capitaux', labelKey: 'nav_capitaux', icon: FileSpreadsheet, group: 'finance' },
  { path: '/marche', labelKey: 'nav_market', icon: Swords, group: 'meta' },
  { path: '/appels-offres', labelKey: 'nav_appels_offres', icon: FileSignature, group: 'meta', requiresCommercial: true },
  { path: '/clients', labelKey: 'nav_clients', icon: UserCircle, group: 'meta' },
  { path: '/concurrence', labelKey: 'nav_competition', icon: Crosshair, group: 'meta' },
  { path: '/strategie', labelKey: 'nav_strategy', icon: LineChart, group: 'meta' },
  { path: '/achievements', labelKey: 'nav_achievements', icon: Trophy, group: 'meta' },
  { path: '/challenges', labelKey: 'nav_challenges', icon: Target, group: 'meta' },
  // 07/02/2026 — Arbre d'atouts (skill tree MMORPG-like).
  { path: '/atouts', labelKey: 'nav_atouts', icon: Sparkles, group: 'meta' },
];

const NAV_GROUPS = [
  { id: 'main', labelKey: 'operations_group' },
  { id: 'finance', labelKey: 'finance_group' },
  { id: 'meta', labelKey: 'strategy_group' },
];

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const restoreAmbient = useAudioStore((s) => s.restoreAmbient);
  const resetGame = useGameStore((s) => s.resetGame);
  const company = useGameStore((s) => s.company);
  // 09/02/2026 — Q2 Lot H : vue trésorerie figée dans sidebar (lecture seule).
  const treasury = useGameStore((s) => s.treasury || 0);
  // 10/02/2026 (Lot J) — Découvert dynamique : récupère le résumé pour le
  // chip sidebar (plafond + utilisation).
  const _bankRelations = useGameStore((s) => s.bankRelations);
  const _creditScore = useGameStore((s) => s.creditScore);
  const _monthlyRevenues = useGameStore((s) => s.monthlyRevenues || 0);
  const overdraftInfo = overdraftSummary({
    treasury, bankRelations: _bankRelations, creditScore: _creditScore, monthlyRevenues: _monthlyRevenues,
  });
  const gameStarted = useGameStore((s) => s.gameStarted);
  const hiredSupport = useGameStore((s) => s.hiredSupport || {});
  const hasCommercialRole = !!(hiredSupport['role-lea-bertrand'] || hiredSupport['role-marc-dupuis'] || hiredSupport['role-sylvie-marchand']);
  // 26/06/2026 — Agenda débloqué quand RH (Nadia) + Exploitation (Rémi) sont
  // tous les deux recrutés. Cf. user 26/06/2026 « OK proposé en conditions ».
  const hasAgendaAccess = !!(hiredSupport['role-nadia-cheikh'] && hiredSupport['role-christophe-faure']);
  const difficulty = useGameStore((s) => s.difficulty);
  const selectedAdvisor = useGameStore((s) => s.selectedAdvisor);
  const advisorEnabled = useGameStore((s) => s.advisorEnabled);
  const toggleAdvisor = useGameStore((s) => s.toggleAdvisor);
  const setAdvisor = useGameStore((s) => s.setAdvisor);
  const { language, setLanguage } = useLanguageStore();
  const theme = useThemeStore((s) => s.theme);
  const cycleTheme = useThemeStore((s) => s.cycleTheme);
  const { t } = useTranslation();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [showAdvisorSettings, setShowAdvisorSettings] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  // 11/02/2026 (Axe 1a) — Modal « Signaler un bug ».
  const [bugModalOpen, setBugModalOpen] = useState(false);
  // 12/02/2026 — Bilan de fin de partie affiché avant le reset via
  // "Quitter la partie". Remplace le window.confirm générique.
  const [endGameSummaryOpen, setEndGameSummaryOpen] = useState(false);
  // 05/02/2026 — Q1 engagement : toggle son sur résultats AO (default false).
  const aoSoundEnabled = useGameStore((s) => s.aoSoundEnabled);
  const setAoSoundEnabled = useGameStore((s) => s.setAoSoundEnabled);
  const toggleAoSound = useCallback(
    () => setAoSoundEnabled(!aoSoundEnabled),
    [aoSoundEnabled, setAoSoundEnabled],
  );

  // Sync presentation mode with native fullscreen state (F11 / Esc / button)
  useEffect(() => {
    const handleFsChange = () => {
      setPresentationMode(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const togglePresentation = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (err) {
      // Fallback: at least toggle the layout to "presentation" without browser fullscreen
      setPresentationMode((p) => !p);
    }
  }, []);

  useEffect(() => {
    restoreAmbient();
  }, [restoreAmbient]);

  useEffect(() => {
    const handleOpenInsights = () => setInsightsOpen(true);
    window.addEventListener('hst:open-insights', handleOpenInsights);
    return () => window.removeEventListener('hst:open-insights', handleOpenInsights);
  }, []);

  useGameSounds();

  const inbox = useGameStore(s => s.inbox);
  const inboxUnread = (inbox || []).filter(m => !m.read).length;

  // Tax alerts — count distinct unpaid taxes on the menu badge.
  const isAnnualDue = useGameStore(s => s.isAnnualDue || 0);
  const cfeDue = useGameStore(s => s.cfeDue || 0);
  const cvaeDue = useGameStore(s => s.cvaeDue || 0);
  const essieuDue = useGameStore(s => s.essieuDue || 0);
  const taxUnpaidCount = [isAnnualDue, cfeDue, cvaeDue, essieuDue].filter(v => v > 0).length;

  // NEW-feature counters per nav path (incremented on support hires).
  const newFeatureCounts = useGameStore(s => s.newFeatureCounts || EMPTY_OBJECT);
  const markNavFeatureSeen = useGameStore(s => s.markNavFeatureSeen);

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    const label = item.labelKey ? t(item.labelKey) : item.label;
    const newCount = newFeatureCounts[item.path] || 0;
    let badgeCount = 0;
    let badgeTitle = '';
    if (item.badge === 'inbox') {
      badgeCount = inboxUnread;
      badgeTitle = t('nav_unread_messages', { count: badgeCount, plural: badgeCount > 1 ? 's' : '' });
    } else if (item.badge === 'tax') {
      badgeCount = taxUnpaidCount;
      const labels = [];
      if (isAnnualDue > 0) labels.push('IS');
      if (cfeDue > 0) labels.push('CFE');
      if (cvaeDue > 0) labels.push('CVAE');
      if (essieuDue > 0) labels.push(t('tax_label_essieu'));
      badgeTitle = labels.length > 0 ? t('nav_taxes_to_pay', { list: labels.join(', ') }) : '';
    }
    const handleClick = () => {
      // Clear the NEW badge when the user actually visits the page.
      if (newCount > 0 && typeof markNavFeatureSeen === 'function') {
        markNavFeatureSeen(item.path);
      }
      navigate(item.path);
    };
    return (
      <button
        onClick={handleClick}
        className={`group relative w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium transition-all rounded-md ${
          isActive
            ? 'bg-accent/10 text-foreground border-l-2 border-accent'
            : 'text-slate-400 hover:bg-ink-700/60 hover:text-slate-100 border-l-2 border-transparent'
        }`}
        data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
      >
        <Icon className={`w-4 h-4 transition-colors ${isActive ? 'text-accent' : 'text-slate-500 group-hover:text-slate-300'}`} />
        <span className="flex-1 text-left tracking-tight">{label}</span>
        {newCount > 0 && (
          <span
            className="text-[9px] font-extrabold bg-emerald-500/90 text-white rounded-md px-1.5 py-[1px] uppercase tracking-wider shadow-[0_0_0_1px_rgba(16,185,129,0.35)]"
            data-testid={`nav-new-${item.path.replace('/', '') || 'dashboard'}`}
            title={t('nav_new_features_tooltip', { count: newCount, plural1: newCount > 1 ? 'les' : '', plural2: newCount > 1 ? 's' : '' })}
          >
            New {newCount}
          </span>
        )}
        {badgeCount > 0 && (
          <span
            className="text-[10px] font-bold font-mono bg-rose-500 text-white rounded-full px-1.5 min-w-[20px] h-[18px] inline-flex items-center justify-center shadow-[0_0_0_2px_rgba(244,63,94,0.15)] animate-pulse-glow"
            data-testid={`nav-badge-${item.path.replace('/', '')}`}
            title={badgeTitle}
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
        {isActive && <ChevronRight className="w-3 h-3 text-accent" />}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-ink-900 text-slate-200">
      {/* Desktop Sidebar — hidden in presentation mode */}
      {!presentationMode && (
      <aside
        className="hidden md:flex flex-col w-60 h-screen fixed left-0 top-0 bg-ink-950 border-r border-ink-600 z-40"
        data-testid="desktop-sidebar"
      >
        {/* Header */}
        <div className="px-4 py-4 border-b border-ink-600">
          <div className="flex items-center gap-2.5 min-w-0">
            {company
              ? <CompanyLogo company={company} size={36} className="ring-1 ring-ink-500 flex-shrink-0" />
              : <HSTLogo size={36} withGlow={false} className="ring-1 ring-ink-500 flex-shrink-0" />}
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-sm font-black tracking-tight truncate text-foreground">{company?.name || 'HST'}</h1>
              <p className="text-[9px] uppercase tracking-[0.18em] text-slate-500 mt-0.5 truncate font-mono">{t('app_subtitle')}</p>
            </div>
          </div>
          {/* Controls row */}
          <div className="flex items-center gap-1.5 mt-3">
            <SoundToggle variant="sidebar" />
            <GameNotifications />
            {difficulty && (
              <span
                className={`ml-auto text-[9px] px-2 py-0.5 rounded-sm font-mono font-semibold tracking-wider uppercase ${
                  difficulty === 'facile'
                    ? 'bg-money/15 text-money border border-money/30'
                    : difficulty === 'difficile'
                    ? 'bg-danger/15 text-danger border border-danger/30'
                    : 'bg-accent/15 text-accent border border-accent/30'
                }`}
                data-testid="difficulty-badge"
              >
                {difficulty === 'facile' ? 'EASY' : difficulty === 'difficile' ? 'HARD' : 'NORM'}
              </span>
            )}
            <button
              onClick={cycleTheme}
              className="text-[9px] px-2 py-0.5 rounded-sm bg-ink-700 text-slate-300 hover:text-foreground hover:bg-ink-600 transition-colors flex items-center gap-1 font-mono font-semibold uppercase tracking-wider border border-ink-600"
              data-testid="theme-toggle"
              title={
                theme === 'dark' ? t('theme_tooltip_dark')
                : theme === 'light' ? t('theme_tooltip_light')
                : t('theme_tooltip_auto')
              }
            >
              {theme === 'dark' && <><Moon className="w-2.5 h-2.5" />DRK</>}
              {theme === 'light' && <><Sun className="w-2.5 h-2.5" />CLR</>}
              {theme === 'auto' && <><MonitorSmartphone className="w-2.5 h-2.5" />AUTO</>}
            </button>
            <button
              onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
              className="text-[9px] px-2 py-0.5 rounded-sm bg-ink-700 text-slate-300 hover:text-foreground hover:bg-ink-600 transition-colors flex items-center gap-1 font-mono font-semibold uppercase tracking-wider border border-ink-600"
              data-testid="language-toggle"
            >
              <Globe className="w-2.5 h-2.5" />
              {language === 'fr' ? 'FR' : 'EN'}
            </button>
          </div>
          {/* 09/02/2026 — Q2 Lot H user prod : vue trésorerie figée sous
              les contrôles (son, notifs, thème, langue). Permet de voir
              le solde en permanence sur toutes les pages, utile lors des
              investissements (« avoir une vue rapide ») sans avoir à
              revenir au Dashboard. */}
          {gameStarted && (
            <div
              className="mt-2 flex flex-col gap-1 px-2.5 py-1.5 rounded-md bg-ink-700/50 border border-ink-600"
              data-testid="sidebar-treasury-chip"
              title="Trésorerie disponible (toujours visible)"
            >
              <div className="flex items-center gap-2">
                <Wallet className="w-3.5 h-3.5 text-money flex-shrink-0" />
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-[8px] uppercase tracking-wider text-slate-500 font-mono">
                    Trésorerie · BNB Mariba
                  </span>
                  <span
                    className={`text-sm font-mono font-bold tabular-nums truncate ${
                      treasury >= 10000 ? 'text-money' : treasury >= 0 ? 'text-amber-300' : 'text-danger'
                    }`}
                    data-testid="sidebar-treasury-value"
                  >
                    {Math.round(treasury || 0).toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>
              {overdraftInfo && (
                <p
                  className={`text-[9px] font-mono tracking-wide pl-5 ${
                    overdraftInfo.tone === 'danger' ? 'text-rose-400'
                    : overdraftInfo.tone === 'warning' ? 'text-amber-400'
                    : 'text-slate-500'
                  }`}
                  data-testid="sidebar-overdraft-info"
                  title="Plafond de découvert dynamique fixé par Clara Fontana (BNB Mariba)"
                >
                  {overdraftInfo.text}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Navigation — grouped */}
        <nav className="flex-1 px-2 py-3 space-y-4 overflow-y-auto">
          {NAV_GROUPS.map(group => (
            <div key={group.id}>
              <p className="overline px-3 mb-1.5">{t(group.labelKey)}</p>
              <div className="space-y-0.5">
                {NAV_ITEMS.filter(it => {
                  if (it.group !== group.id) return false;
                  if (it.requiresCommercial && !hasCommercialRole) return false;
                  if (it.requiresAgenda && !hasAgendaAccess) return false;
                  return true;
                }).map(it => (
                  <NavLink key={it.path} item={it} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div className="px-2 py-3 border-t border-ink-600 space-y-1">
          {/* Strategic insights — always available, even without an advisor */}
          <button
            onClick={() => setInsightsOpen(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium text-accent hover:bg-accent/10 hover:text-foreground transition-colors border border-accent/30 bg-accent/5"
            data-testid="btn-strategic-insights"
            title={t('strategic_insights_tooltip')}
          >
            <Brain className="w-4 h-4" />
            <span className="flex-1 text-left">{t('strategic_insights_label')}</span>
            <ChevronRight className="w-3 h-3 opacity-60" />
          </button>

          {/* Advisor block */}
          <button
            onClick={() => setShowAdvisorSettings(!showAdvisorSettings)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium text-slate-400 hover:bg-ink-700/60 hover:text-slate-100 transition-colors"
            data-testid="btn-advisor-settings"
          >
            <UserCircle className="w-4 h-4" />
            <span className="flex-1 text-left">{t('advisor_label')}</span>
            {selectedAdvisor && advisorEnabled && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-money/15 text-money font-mono uppercase tracking-wider">
                {ADVISORS[selectedAdvisor]?.shortName}
              </span>
            )}
          </button>

          {showAdvisorSettings && (
            <div className="bg-ink-700/50 border border-ink-600 rounded-md p-2.5 space-y-2 animate-in slide-in-from-top-2 duration-200" data-testid="advisor-settings-panel">
              <button
                onClick={toggleAdvisor}
                className="w-full flex items-center justify-between text-[11px]"
                data-testid="advisor-toggle"
              >
                <span className="text-slate-400">{t('advisor_active')}</span>
                {advisorEnabled
                  ? <ToggleRight className="w-5 h-5 text-money" />
                  : <ToggleLeft className="w-5 h-5 text-slate-600" />}
              </button>

              {advisorEnabled && (
                <div className="space-y-1">
                  {Object.values(ADVISORS).map(adv => (
                    <button
                      key={adv.id}
                      onClick={() => setAdvisor(adv.id)}
                      className={`w-full flex items-center gap-2 px-1.5 py-1.5 rounded-sm text-[10px] transition-colors ${
                        selectedAdvisor === adv.id
                          ? 'bg-accent/15 text-white border border-accent/40'
                          : 'text-slate-400 hover:bg-ink-700 hover:text-slate-200 border border-transparent'
                      }`}
                      data-testid={`advisor-switch-${adv.id}`}
                    >
                      <img src={adv.portrait} alt={adv.shortName} className="w-7 h-7 rounded-full object-cover object-top ring-1 ring-ink-500" />
                      <div className="text-left flex-1 min-w-0">
                        <p className="font-medium truncate">{adv.shortName}</p>
                        <p className="text-[8px] text-slate-500 truncate uppercase tracking-wider font-mono">{adv.title}</p>
                      </div>
                      {selectedAdvisor === adv.id && (
                        <span className="text-[8px] text-money font-mono uppercase tracking-wider">{t('active_status_short')}</span>
                      )}
                    </button>
                  ))}
                  {!selectedAdvisor && (
                    <p className="text-[10px] text-slate-500 text-center py-1">{t('no_advisor_selected')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <button
            onClick={togglePresentation}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium text-slate-400 hover:bg-ink-700/60 hover:text-slate-100 transition-colors"
            data-testid="btn-presentation"
            title={t('presentation_mode_title')}
          >
            <Maximize2 className="w-4 h-4" />
            {t('presentation_label')}
            <span className="ml-auto text-[8px] font-mono text-slate-600 uppercase tracking-wider">F11</span>
          </button>
          {/* 05/02/2026 — Q1 engagement : toggle son sur résultats AO */}
          <button
            onClick={toggleAoSound}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium text-slate-400 hover:bg-ink-700/60 hover:text-slate-100 transition-colors"
            data-testid="btn-ao-sound-toggle"
            title="Activer le son sur résultat AO"
          >
            {aoSoundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            <span className="flex-1 text-left">Sons résultats AO{/* i18n-keep — narrative FR */}</span>
            {aoSoundEnabled
              ? <ToggleRight className="w-5 h-5 text-money" />
              : <ToggleLeft className="w-5 h-5 text-slate-600" />}
          </button>
          <button
            onClick={() => setSaveModalOpen(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium text-slate-400 hover:bg-ink-700/60 hover:text-slate-100 transition-colors"
            data-testid="btn-save-game"
          >
            <Save className="w-4 h-4" />
            {t('save')}
          </button>
          {/* 11/02/2026 (Axe 1a) — Bouton « Signaler un bug » côté apprenant */}
          <button
            onClick={() => setBugModalOpen(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium text-slate-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
            data-testid="btn-report-bug"
            title="Signaler un bug ou un problème au formateur"
          >
            <Bug className="w-4 h-4" />
            Signaler un bug
          </button>
          {/* 11/02/2026 (Phase 2 pédagogie) — Export bilan pédagogique PDF */}
          <button
            onClick={async () => {
              try {
                const { default: generateSessionSummaryPDF } = await import('../../utils/generateSessionSummaryPDF');
                generateSessionSummaryPDF(useGameStore.getState());
              } catch (err) {
                console.warn('[Layout] session summary PDF failed:', err);
                window.alert('Impossible de générer le bilan pédagogique.');
              }
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium text-slate-400 hover:bg-accent/10 hover:text-accent transition-colors"
            data-testid="btn-export-session-summary"
            title="Télécharger un bilan pédagogique PDF de la session"
          >
            <FileText className="w-4 h-4" />
            Bilan pédagogique
          </button>
          {/* 18/09/2026 (REX apprenant) — l'apprenant télécharge lui-même sa
              fiche PDF (même document que celui du formateur / admin). */}
          <button
            onClick={async () => {
              const state = useGameStore.getState();
              if (!state.gameStarted) { window.alert(t('my_profile_pdf_not_started')); return; }
              try {
                const [{ buildSnapshot }, { default: generatePlayerProfilePDF }] = await Promise.all([
                  import('../../hooks/useTelemetryReporter'),
                  import('../../utils/generatePlayerProfilePDF'),
                ]);
                const lic = useLicenseStore.getState();
                const holder = lic.holderName || state.company?.name || 'apprenant';
                const slug = String(holder).toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
                generatePlayerProfilePDF({
                  snapshot: buildSnapshot(state, lic.licenseKey || 'demo'),
                  license: { holder_name: holder },
                  download: true,
                  filename: `hst-fiche-${slug}.pdf`,
                });
              } catch (err) {
                console.warn('[Layout] learner profile PDF failed:', err);
                window.alert(t('my_profile_pdf_error'));
              }
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium text-slate-400 hover:bg-accent/10 hover:text-accent transition-colors"
            data-testid="btn-export-my-profile"
            title={t('btn_my_profile_pdf_title')}
          >
            <FileBadge className="w-4 h-4" />
            {t('btn_my_profile_pdf')}
          </button>
          <button
            onClick={() => setEndGameSummaryOpen(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] font-medium text-danger/80 hover:bg-danger/10 hover:text-danger transition-colors"
            data-testid="btn-quit-game"
          >
            <LogOut className="w-4 h-4" />
            {t('quit')}
          </button>
        </div>
      </aside>
      )}

      {/* Mobile Top Bar */}
      {!presentationMode && (
      <div className="md:hidden fixed top-0 left-0 right-0 h-12 bg-ink-950 border-b border-ink-600 z-50 flex items-center justify-between px-4" data-testid="mobile-top-bar">
        <div className="flex items-center gap-2 min-w-0">
          {company
            ? <CompanyLogo company={company} size={24} className="ring-1 ring-ink-500" />
            : <HSTLogo size={24} withGlow={false} className="ring-1 ring-ink-500" />}
          <h1 className="font-display text-foreground font-bold text-sm truncate">{company?.name || 'HST'}</h1>
        </div>
        <div className="flex items-center gap-1">
          <SoundToggle variant="mobile" />
          <GameNotifications />
        </div>
      </div>
      )}

      {/* Mobile Bottom Nav */}
      {!presentationMode && (
      <nav
        className="fixed bottom-0 left-0 right-0 h-16 bg-ink-950 border-t border-ink-600 flex justify-around items-center z-50 md:hidden"
        data-testid="mobile-bottom-nav"
      >
        {NAV_ITEMS.filter(i => {
          if (i.group !== 'main') return false;
          if (i.requiresAgenda && !hasAgendaAccess) return false;
          return true;
        }).map(item => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          const label = item.labelKey ? t(item.labelKey) : item.label;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center text-[10px] transition-colors ${
                isActive ? 'text-accent font-semibold' : 'text-slate-500 hover:text-slate-300'
              }`}
              data-testid={`mobile-nav-${item.path.replace('/', '') || 'dashboard'}`}
            >
              <Icon className="w-5 h-5 mb-0.5" />
              <span>{label}</span>
            </button>
          );
        })}
      </nav>
      )}

      {/* Floating exit-presentation pill (visible only in presentation mode) */}
      {presentationMode && (
        <button
          onClick={togglePresentation}
          className="fixed top-3 right-3 z-[60] bg-ink-800/90 backdrop-blur-md border border-ink-600 hover:border-accent text-slate-200 hover:text-foreground rounded-full px-3 py-1.5 text-[11px] font-mono font-semibold uppercase tracking-wider flex items-center gap-1.5 shadow-lg transition-all"
          data-testid="btn-exit-presentation"
          title={t('exit_presentation_title')}
        >
          <Minimize2 className="w-3 h-3" />
          {t('exit_presentation')}
          <span className="text-[8px] text-slate-500 ml-1">[ESC]</span>
        </button>
      )}

      {/* Main content */}
      <main className={`${presentationMode ? 'ml-0 pt-0' : 'md:ml-60 pb-20 md:pb-4 pt-12 md:pt-0'} min-h-screen transition-[margin] duration-200`}>
        <div className={`${presentationMode ? 'w-full max-w-none px-6 lg:px-10 py-6' : 'w-full max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6'}`}>
          {children}
        </div>
      </main>

      <SaveGameModal open={saveModalOpen} onClose={() => setSaveModalOpen(false)} />
      {insightsOpen && <AdvisorInsightsPanel onClose={() => setInsightsOpen(false)} />}
      <TradeShowModal />
      <PostponedEventsBadge />
      <ContextualTutoCard />
      <ContextualTutoTriggers />
      <MilestoneVideoModal />
      {bugModalOpen && <BugReportModal open={bugModalOpen} onClose={() => setBugModalOpen(false)} />}
      {endGameSummaryOpen && (
        <EndGameSummaryModal
          state={useGameStore.getState()}
          onCancel={() => setEndGameSummaryOpen(false)}
          onConfirm={() => {
            setEndGameSummaryOpen(false);
            resetGame();
            navigate('/');
          }}
        />
      )}
    </div>
  );
};

export default Layout;
